"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SensitivityChart = exports.ValueBars = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_components_1 = __importDefault(require("styled-components"));
const format_1 = require("./format");
const model_1 = require("./model");
/**
 * Print diagrams, drawn as SVG rather than with coloured backgrounds like their
 * counterparts on screen. Browsers drop background fills when printing unless
 * the reader has ticked "background graphics", so a CSS bar prints as an empty
 * gap. An SVG rect is page content and always comes out.
 */
const INK = {
    navy: "#092960",
    slate: "#36498d",
    rule: "#a5abb0",
    ets: "#227dfe",
    pool: "#19a476",
    penalty: "#4ddfb5",
    cost: "#d12e28",
};
const Block = styled_components_1.default.div `
  margin-top: 2mm;
  break-inside: avoid;
`;
const Row = styled_components_1.default.div `
  display: grid;
  grid-template-columns: 52mm 1fr 22mm;
  align-items: center;
  gap: 2.5mm;
  margin-bottom: 0.9mm;
  font-size: 8pt;
  color: ${INK.navy};
`;
const Value = styled_components_1.default.span `
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Caption = styled_components_1.default.p `
  margin: 1mm 0 0;
  font-size: 7.5pt;
  color: ${INK.slate};
`;
/** Relative size of each component of the result. */
const ValueBars = ({ result }) => {
    const items = [
        { label: "EU ETS cost avoided", value: result.ets, fill: INK.ets },
        {
            label: "Profit from pooling",
            value: result.poolProfit,
            fill: INK.pool,
        },
        {
            label: "FuelEU penalty avoided",
            value: result.penaltyAvoided,
            fill: INK.penalty,
        },
        {
            label: "Biofuel premium paid",
            value: -result.premium,
            fill: INK.cost,
        },
    ];
    const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
    return ((0, jsx_runtime_1.jsxs)(Block, { children: [items.map((item) => ((0, jsx_runtime_1.jsxs)(Row, { children: [(0, jsx_runtime_1.jsx)("span", { children: item.label }), (0, jsx_runtime_1.jsx)("svg", { viewBox: "0 0 100 5", preserveAspectRatio: "none", width: "100%", height: "2.6mm", role: "img", "aria-label": `${item.label}: ${(0, format_1.formatInt)(item.value)} USD`, children: (0, jsx_runtime_1.jsx)("rect", { x: "0", y: "0", width: Math.max((Math.abs(item.value) / max) * 100, 0.4), height: "5", fill: item.fill }) }), (0, jsx_runtime_1.jsx)(Value, { children: (0, format_1.formatCompact)(item.value) })] }, item.label))), (0, jsx_runtime_1.jsx)(Caption, { children: "Bars are to scale against the largest figure. The two surplus routes are alternatives, so only one of them and the ETS saving apply at once." })] }));
};
exports.ValueBars = ValueBars;
/** Net result of the pooling route across the plausible range of biofuel intensity. */
const SensitivityChart = ({ inputs }) => {
    const points = model_1.SENSITIVITY_INTENSITIES.map((ciBio) => ({
        ciBio,
        net: (0, model_1.calculate)(inputs, ciBio).poolNet,
        here: Math.abs(ciBio - inputs.ciBio) < 0.5,
    }));
    const values = points.map((p) => p.net);
    const top = Math.max(...values, 0);
    const bottom = Math.min(...values, 0);
    const span = top - bottom || 1;
    // Plot area inside a 500 x 132 viewBox, leaving room for labels above and below.
    const PLOT_TOP = 20;
    const PLOT_H = 74;
    const zeroY = PLOT_TOP + ((top - 0) / span) * PLOT_H;
    const colW = 500 / points.length;
    const barW = colW * 0.44;
    return ((0, jsx_runtime_1.jsxs)(Block, { children: [(0, jsx_runtime_1.jsxs)("svg", { viewBox: "0 0 500 132", width: "100%", height: "28mm", role: "img", children: [(0, jsx_runtime_1.jsx)("line", { x1: "0", y1: zeroY, x2: "500", y2: zeroY, stroke: INK.rule, strokeWidth: "0.8" }), points.map((p, i) => {
                        const cx = i * colW + colW / 2;
                        const y = PLOT_TOP + ((top - Math.max(p.net, 0)) / span) * PLOT_H;
                        const h = Math.max((Math.abs(p.net) / span) * PLOT_H, 1);
                        return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("rect", { x: cx - barW / 2, y: p.net >= 0 ? y : zeroY, width: barW, height: h, fill: p.net >= 0 ? INK.pool : INK.cost, opacity: p.here ? 1 : 0.55 }), (0, jsx_runtime_1.jsx)("text", { x: cx, y: p.net >= 0 ? y - 5 : zeroY + h + 12, textAnchor: "middle", fontSize: "12", fontWeight: p.here ? 700 : 400, fill: p.net >= 0 ? INK.navy : INK.cost, children: (0, format_1.formatCompact)(p.net) }), (0, jsx_runtime_1.jsxs)("text", { x: cx, y: "128", textAnchor: "middle", fontSize: "11", fontWeight: p.here ? 700 : 400, fill: INK.slate, children: [p.ciBio, " g/MJ"] })] }, p.ciBio));
                    })] }), (0, jsx_runtime_1.jsx)(Caption, { children: "Net result of pooling the surplus at other certified biofuel intensities. Your figure is the solid bar; a lower intensity yields more surplus from the same tonnage." })] }));
};
exports.SensitivityChart = SensitivityChart;
